import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export type Theme = 'light' | 'dark' | 'system';

export interface UIState {
  // Theme management
  theme: Theme;
  effectiveTheme: 'light' | 'dark';
  
  // Navigation state
  sidebarOpen: boolean;
  sidebarCollapsed: boolean;
  
  // Loading states
  globalLoading: boolean;
  loadingMessage: string | null;
  
  // Error states
  globalError: string | null;
  errorDetails: any | null;
  
  // Modal/dialog states
  activeModal: string | null;
  modalData: any | null;
  
  // Accessibility preferences
  reducedMotion: boolean;
  highContrast: boolean;
  fontSize: 'small' | 'medium' | 'large';
  
  // Notification state
  notifications: Notification[];

  // Chat experience preferences
  /**
   * When true, longer AI replies render as multiple sequential bubbles
   * instead of one. See docs/plans/multi-bubble-ai-replies-implementation-plan.md.
   */
  multiBubbleEnabled: boolean;

  // Actions
  setTheme: (theme: Theme) => void;
  updateEffectiveTheme: () => void;
  setSidebarOpen: (open: boolean) => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  toggleSidebar: () => void;
  setGlobalLoading: (loading: boolean, message?: string) => void;
  setGlobalError: (error: string | null, details?: any) => void;
  clearGlobalError: () => void;
  openModal: (modalId: string, data?: any) => void;
  closeModal: () => void;
  setReducedMotion: (enabled: boolean) => void;
  setHighContrast: (enabled: boolean) => void;
  setFontSize: (size: 'small' | 'medium' | 'large') => void;
  addNotification: (notification: Omit<Notification, 'id'>) => void;
  removeNotification: (id: string) => void;
  clearAllNotifications: () => void;
  setMultiBubbleEnabled: (enabled: boolean) => void;
}

export interface Notification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message?: string;
  duration?: number; // ms, null for persistent
  action?: {
    label: string;
    callback: () => void;
  };
  createdAt: string;
}

// Helper to detect system theme preference
const getSystemTheme = (): 'light' | 'dark' => {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

// Helper to detect reduced motion preference
const getSystemReducedMotion = (): boolean => {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

export const useUIStore = create<UIState>()(
  persist(
    (set, get) => ({
      // Initial theme state
      theme: 'system',
      effectiveTheme: getSystemTheme(),
      
      // Navigation state
      sidebarOpen: true,
      sidebarCollapsed: false,
      
      // Loading states
      globalLoading: false,
      loadingMessage: null,
      
      // Error states
      globalError: null,
      errorDetails: null,
      
      // Modal states
      activeModal: null,
      modalData: null,
      
      // Accessibility preferences
      reducedMotion: getSystemReducedMotion(),
      highContrast: false,
      fontSize: 'medium',
      
      // Notifications
      notifications: [],

      // Chat experience preferences
      multiBubbleEnabled: true,

      setTheme: (theme) => {
        set({ theme });
        get().updateEffectiveTheme();
      },

      updateEffectiveTheme: () => {
        const { theme } = get();
        const effectiveTheme = theme === 'system' ? getSystemTheme() : theme;
        set({ effectiveTheme });
        
        // Update document class for CSS theming
        if (typeof document !== 'undefined') {
          document.documentElement.classList.remove('light', 'dark');
          document.documentElement.classList.add(effectiveTheme);
        }
      },

      setSidebarOpen: (sidebarOpen) => {
        set({ sidebarOpen });
      },

      setSidebarCollapsed: (sidebarCollapsed) => {
        set({ sidebarCollapsed });
      },

      toggleSidebar: () => {
        const { sidebarOpen } = get();
        set({ sidebarOpen: !sidebarOpen });
      },

      setGlobalLoading: (globalLoading, loadingMessage?: string) => {
        set({ globalLoading, loadingMessage: loadingMessage || null });
      },

      setGlobalError: (globalError, errorDetails = null) => {
        set({ globalError, errorDetails, globalLoading: false });
      },

      clearGlobalError: () => {
        set({ globalError: null, errorDetails: null });
      },

      openModal: (activeModal, modalData = null) => {
        set({ activeModal, modalData });
      },

      closeModal: () => {
        set({ activeModal: null, modalData: null });
      },

      setReducedMotion: (reducedMotion) => {
        set({ reducedMotion });
        
        // Update CSS custom property
        if (typeof document !== 'undefined') {
          document.documentElement.style.setProperty(
            '--motion-duration', 
            reducedMotion ? '0ms' : '300ms'
          );
        }
      },

      setHighContrast: (highContrast) => {
        set({ highContrast });
        
        // Update document class
        if (typeof document !== 'undefined') {
          document.documentElement.classList.toggle('high-contrast', highContrast);
        }
      },

      setFontSize: (fontSize) => {
        set({ fontSize });
        
        // Update CSS custom property
        if (typeof document !== 'undefined') {
          const sizeMap = { small: '14px', medium: '16px', large: '18px' };
          document.documentElement.style.setProperty(
            '--base-font-size', 
            sizeMap[fontSize]
          );
        }
      },

      addNotification: (notification) => {
        const id = `notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const newNotification: Notification = {
          ...notification,
          id,
          createdAt: new Date().toISOString()
        };
        
        const { notifications } = get();
        set({ notifications: [newNotification, ...notifications] });
        
        // Auto-remove notification if duration is specified
        if (notification.duration && notification.duration > 0) {
          setTimeout(() => {
            get().removeNotification(id);
          }, notification.duration);
        }
      },

      removeNotification: (id) => {
        const { notifications } = get();
        set({ notifications: notifications.filter(n => n.id !== id) });
      },

      clearAllNotifications: () => {
        set({ notifications: [] });
      },

      setMultiBubbleEnabled: (multiBubbleEnabled) => {
        set({ multiBubbleEnabled });
      }
    }),
    {
      name: 'converlang-ui',
      storage: createJSONStorage(() => localStorage),
      // Exclude temporary states from persistence
      partialize: (state) => ({
        theme: state.theme,
        sidebarCollapsed: state.sidebarCollapsed,
        reducedMotion: state.reducedMotion,
        highContrast: state.highContrast,
        fontSize: state.fontSize,
        multiBubbleEnabled: state.multiBubbleEnabled,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          // Re-apply theme and accessibility settings on hydration
          state.updateEffectiveTheme();
          
          if (typeof document !== 'undefined') {
            document.documentElement.classList.toggle('high-contrast', state.highContrast);
            
            const sizeMap = { small: '14px', medium: '16px', large: '18px' };
            document.documentElement.style.setProperty(
              '--base-font-size', 
              sizeMap[state.fontSize]
            );
            
            document.documentElement.style.setProperty(
              '--motion-duration', 
              state.reducedMotion ? '0ms' : '300ms'
            );
          }
        }
      }
    }
  )
);

// Hook to listen for system theme changes
export const useSystemThemeListener = () => {
  const { theme, updateEffectiveTheme } = useUIStore();
  
  React.useEffect(() => {
    if (theme !== 'system') return;
    
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => updateEffectiveTheme();
    
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme, updateEffectiveTheme]);
};

// Hook to listen for system reduced motion changes
export const useSystemMotionListener = () => {
  const { setReducedMotion } = useUIStore();
  
  React.useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handleChange = () => setReducedMotion(mediaQuery.matches);
    
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [setReducedMotion]);
};

import React from 'react';